/**
 * What this browser downloaded, so a later visit can ask "did it fit?". Kept
 * in localStorage per brand; never sent anywhere. One record per design and
 * params (a new download of the same bracket refreshes it).
 */
export interface DownloadRecord {
  designId: string;
  version: string;
  params: Record<string, unknown>;
  /** Name shown in the reminder ("Wheel cradles · WD42 WT12 WS4 HA45 M"). */
  name: string;
  /** Community entry the params came from, when downloaded unchanged. */
  entryId?: string;
  /** Set the visitor came for (`?fit=`), if any. */
  fit?: string;
  /** ISO time of the last download. */
  at: string;
  /** Answered (printed, adjusting) or dismissed: never asked again. */
  answered?: boolean;
}

/** Most records kept; the oldest go first. */
export const MAX_DOWNLOAD_RECORDS = 30;

/** Stable key of a params object (key order doesn't matter). */
export function paramsSignature(params: Record<string, unknown>): string {
  return JSON.stringify(Object.keys(params).sort().map((k) => [k, params[k]]));
}

const same = (a: Pick<DownloadRecord, "designId" | "params">, b: Pick<DownloadRecord, "designId" | "params">) =>
  a.designId === b.designId && paramsSignature(a.params) === paramsSignature(b.params);

/** The log with `rec` first: replaces the record of the same design and params (asked again later), keeps at most `MAX_DOWNLOAD_RECORDS`. */
export function addDownload(log: DownloadRecord[], rec: DownloadRecord): DownloadRecord[] {
  return [rec, ...log.filter((r) => !same(r, rec))].slice(0, MAX_DOWNLOAD_RECORDS);
}

/** The log with the record for these params marked answered. */
export function answerDownload(log: DownloadRecord[], key: Pick<DownloadRecord, "designId" | "params">): DownloadRecord[] {
  return log.map((r) => (same(r, key) ? { ...r, answered: true } : r));
}

/** The newest unanswered download of one of `designIds` made before `before` (this visit's own downloads don't count). */
export function pendingDownload(log: DownloadRecord[], designIds: string[], before: string): DownloadRecord | undefined {
  return log.find((r) => !r.answered && designIds.includes(r.designId) && r.at < before);
}

const storageKey = (brand: string) => `cfg-downloads:${brand}`;

export function readDownloadLog(brand: string): DownloadRecord[] {
  try {
    const raw = localStorage.getItem(storageKey(brand));
    const parsed = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(parsed) ? (parsed as DownloadRecord[]) : [];
  } catch {
    return [];
  }
}

export function writeDownloadLog(brand: string, log: DownloadRecord[]): void {
  try {
    localStorage.setItem(storageKey(brand), JSON.stringify(log));
  } catch {
    /* private mode or full storage: the reminder just won't come */
  }
}
